import { useEffect } from "react";

/**
 * SEO — sets document.title + meta description + canonical + Open Graph tags.
 * Pure DOM manipulation (works in every React version without extra deps).
 * Cleans up meta values on unmount so navigating between pages leaves a clean head.
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  siteName = "Nimisha Mohan Srivastava",
}) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} · ${siteName}`
      : `${siteName} — Executive Portfolio & Insights`;
    document.title = fullTitle;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return el;
    };

    const setMeta = (name, content) => {
      if (!content) return null;
      const el = ensure(`meta[name="${name}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", name);
        return m;
      });
      el.setAttribute("content", content);
      return el;
    };

    const setProp = (property, content) => {
      if (!content) return null;
      const el = ensure(`meta[property="${property}"]`, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", property);
        return m;
      });
      el.setAttribute("content", content);
      return el;
    };

    setMeta("description", description);
    setMeta("author", siteName);

    const canonical = url || window.location.href;
    const link = ensure('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    });
    link.setAttribute("href", canonical);

    setProp("og:title", fullTitle);
    setProp("og:description", description);
    setProp("og:type", type);
    setProp("og:url", canonical);
    setProp("og:site_name", siteName);
    if (image) setProp("og:image", image);

    setMeta("twitter:card", image ? "summary_large_image" : "summary");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    if (image) setMeta("twitter:image", image);
  }, [title, description, image, url, type, siteName]);

  return null;
}
